// マウスホイール / Shift+ホイール / Ctrl+ホイールの統合ハンドラ。
// - ホイール（縦）: 縦パン
// - トラックパッド 2 本指スクロール: deltaX / deltaY で縦横同時パン
// - Shift + ホイール: 横パン
// - Ctrl + ホイール: マウス位置中心のズーム（マウスは段階、トラックパッドのピンチは無段階）

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { applyWheelZoom } from "../../../lib/canvas-coordinates";

const WHEEL_PAN_SCALE = 1; // wheel delta をそのまま pan 量へ反映

export function useWheelPanZoom(containerRef: RefObject<HTMLElement | null>): void {
  const setView = useCanvasStore((s) => s.setView);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const { view } = useCanvasStore.getState();
      if (event.ctrlKey || event.metaKey) {
        // Ctrl + ホイール: マウス位置中心のズーム。
        // マウスホイールは従来どおり段階ズーム、トラックパッドの 2 本指ピンチ（ブラウザが
        // ctrlKey 付き wheel として合成する）は指の動きに追従する無段階ズームへ振り分ける。
        setView(applyWheelZoom(view, { x: event.clientX, y: event.clientY }, event));
        return;
      }
      if (event.shiftKey) {
        // Shift + ホイール: 横パン（縦回転しか持たないマウスホイールで横移動するための操作）
        setView({ ...view, panX: view.panX - event.deltaY * WHEEL_PAN_SCALE });
        return;
      }
      // 通常ホイール / トラックパッド 2 本指スクロール: 縦横パン。
      // トラックパッドは横方向に deltaX、縦方向に deltaY を同時に発生させるため両軸を反映する。
      // 通常のマウスホイールは deltaX がほぼ 0 のため、従来どおり縦パンのみとなり回帰しない。
      setView({
        ...view,
        panX: view.panX - event.deltaX * WHEEL_PAN_SCALE,
        panY: view.panY - event.deltaY * WHEEL_PAN_SCALE,
      });
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [containerRef, setView]);
}
